import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  DollarSign, Users, FileText, ShoppingBag, Shield, Ban, Menu, LogOut, Globe, 
  TrendingUp, UserPlus, Calendar, MapPin, Clock, CheckCircle, XCircle, Eye, Image as ImageIcon,
  CreditCard, Receipt, ArrowUpRight, Wallet, Trash2, HandCoins,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import AdminCityContent from '../components/AdminCityContent';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import { photoSrc } from '../utils/photo';

const STRIPE_DASHBOARD_URL = 'https://dashboard.stripe.com/payments';

const CATEGORY_LABELS = {
  yacht: { en: 'Yacht', es: 'Yate' },
  tour: { en: 'Tour', es: 'Tour' },
  activity: { en: 'Activity', es: 'Actividad' },
  party: { en: 'Party', es: 'Fiesta' },
  car_rental: { en: 'Car Rental', es: 'Renta de Auto' },
  lodging: { en: 'Lodging', es: 'Hospedaje' }
};

const LOCATION_LABELS = {
  playa_del_carmen: 'Playa del Carmen',
  cancun: 'Cancún',
  tulum: 'Tulum',
  quintana_roo: 'Quintana Roo',
  los_cabos: 'Los Cabos'
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, api } = useAuth();
  const { language, toggleLanguage } = useLanguage();

  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestsDetailed, setRequestsDetailed] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [tourists, setTourists] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filters
  const [touristFilter, setTouristFilter] = useState('all');
  const [requestFilter, setRequestFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, reqRes, bookRes, provRes, partRes] = await Promise.all([
        api().get('/admin/stats'),
        api().get('/admin/requests'),
        api().get('/admin/bookings'),
        api().get('/admin/providers'),
        api().get('/admin/partners'),
      ]);
      setStats(statsRes.data);
      setRequests(reqRes.data);
      setBookings(bookRes.data);
      setProviders(provRes.data);
      setPartners(partRes.data);
    } catch (error) {
      toast.error('Error loading admin data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchTourists = useCallback(async (period = null) => {
    try {
      const url = period && period !== 'all' ? `/admin/tourists?period=${period}` : '/admin/tourists';
      const res = await api().get(url);
      setTourists(res.data);
    } catch (error) {
      console.error('Error fetching tourists:', error);
    }
  }, [api]);

  const fetchRequestsDetailed = useCallback(async (period = null) => {
    try {
      const url = period && period !== 'all' ? `/admin/requests-detailed?period=${period}` : '/admin/requests-detailed';
      const res = await api().get(url);
      setRequestsDetailed(res.data);
    } catch (error) {
      console.error('Error fetching detailed requests:', error);
    }
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await fetchData();
      await fetchTourists();
      await fetchRequestsDetailed();
    };
    run();
    return () => { cancelled = true; };
  }, [fetchData, fetchTourists, fetchRequestsDetailed]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await fetchTourists(touristFilter); })();
    return () => { cancelled = true; };
  }, [touristFilter, fetchTourists]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await fetchRequestsDetailed(requestFilter); })();
    return () => { cancelled = true; };
  }, [requestFilter, fetchRequestsDetailed]);

  const handleVerifyProvider = async (providerId) => {
    try {
      await api().put(`/admin/providers/${providerId}/verify`);
      toast.success(language === 'en' ? 'Provider verified' : 'Proveedor verificado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleBanProvider = async (providerId) => {
    try {
      await api().put(`/admin/providers/${providerId}/ban`);
      toast.success(language === 'en' ? 'Provider banned' : 'Proveedor baneado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm(language === 'en'
      ? 'Delete this request permanently? This cannot be undone.'
      : '¿Borrar esta solicitud para siempre? No se puede deshacer.')) return;
    try {
      await api().delete(`/admin/requests/${requestId}`);
      toast.success(language === 'en' ? 'Request deleted' : 'Solicitud borrada');
      fetchRequestsDetailed(requestFilter);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteProvider = async (providerId, providerName) => {
    if (!window.confirm(language === 'en'
      ? `Delete provider "${providerName}" permanently? This deletes their products and pending bookings. Paid bookings will block deletion.`
      : `¿Borrar al proveedor "${providerName}" para siempre? Esto borra sus productos y reservas pendientes. Reservas pagadas bloquearán el borrado.`)) return;
    try {
      await api().delete(`/admin/providers/${providerId}`);
      toast.success(language === 'en' ? 'Provider deleted' : 'Proveedor borrado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeletePartner = async (partnerId, partnerName) => {
    if (!window.confirm(language === 'en'
      ? `Delete partner "${partnerName}" permanently? Their referrals stay but stop earning commissions.`
      : `¿Borrar al partner "${partnerName}" para siempre? Sus referidos se mantienen pero ya no generarán comisión.`)) return;
    try {
      await api().delete(`/admin/partners/${partnerId}`);
      toast.success(language === 'en' ? 'Partner deleted' : 'Partner borrado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  return (
    <MobileShell>
      <TopBar
        showBrand
        right={
          <>
            <span className="ml-1 text-[10px] bg-foreground text-background px-2 py-0.5 rounded-full font-bold">ADMIN</span>
            <button onClick={handleLogout} data-testid="admin-logout" className="ml-1 w-9 h-9 rounded-full flex items-center justify-center text-destructive hover:bg-muted">
              <LogOut size={18} />
            </button>
          </>
        }
      />
      <main className="px-4 py-4" data-testid="admin-dashboard">
        {/* Revenue hero — 17% + IVA breakdown */}
        {stats && (
          <div data-testid="admin-revenue-hero" className="mb-5 rounded-3xl bg-gradient-to-br from-caribbean to-caribbean/70 p-5 shadow-xl text-white">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-xs opacity-80 flex items-center gap-1">
                  <Wallet size={12} /> {language === 'en' ? 'Total reservation fees collected' : 'Total cobrado por reservaciones'}
                </p>
                <p className="font-heading text-4xl font-bold leading-tight mt-1">
                  ${(stats.gross_reservations ?? stats.total_revenue ?? 0).toFixed(2)}
                  <span className="text-sm font-normal opacity-80 ml-2">USD</span>
                </p>
                <p className="text-[11px] opacity-80 mt-1">
                  {language === 'en' ? '17% commission + 16% IVA on each booking' : '17% comisión + 16% IVA por reserva'}
                </p>
              </div>
              <a
                href={STRIPE_DASHBOARD_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-stripe-dashboard"
                className="shrink-0 inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full px-3 py-2 text-xs font-semibold transition-colors"
              >
                <CreditCard size={14} />
                {language === 'en' ? 'Stripe' : 'Stripe'}
                <ArrowUpRight size={12} />
              </a>
            </div>

            {/* Inline split: net vs IVA */}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-2xl bg-white/15 backdrop-blur-sm p-3">
                <p className="text-[10px] uppercase tracking-wider opacity-85">{language === 'en' ? 'Net commission (17%)' : 'Comisión neta (17%)'}</p>
                <p className="font-heading text-xl font-bold mt-0.5">
                  ${(stats.net_platform_revenue ?? 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-2xl bg-white/15 backdrop-blur-sm p-3">
                <p className="text-[10px] uppercase tracking-wider opacity-85">{language === 'en' ? 'IVA collected (16%)' : 'IVA recaudado (16%)'}</p>
                <p className="font-heading text-xl font-bold mt-0.5">
                  ${(stats.iva_collected ?? 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Secondary stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
            <Card className="shadow-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
                  <TrendingUp size={12} /> GMV
                </div>
                <p className="font-heading text-lg font-bold">${(stats.gmv ?? 0).toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground">{language === 'en' ? 'Total experience sold' : 'Total vendido'}</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
                  <Receipt size={12} /> {language === 'en' ? 'Cash to providers' : 'En manos del provider'}
                </div>
                <p className="font-heading text-lg font-bold">${(stats.cash_to_providers ?? 0).toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground">{language === 'en' ? 'Paid on arrival' : 'Pagado al llegar'}</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
                  <ShoppingBag size={12} className="text-rosa" /> {language === 'en' ? 'Bookings' : 'Reservas'}
                </div>
                <p className="font-heading text-lg font-bold">{stats.total_bookings}</p>
                <p className="text-[10px] text-muted-foreground">{stats.cancelled_bookings} {language === 'en' ? 'cancelled' : 'canceladas'}</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
                  <FileText size={12} className="text-gold" /> {language === 'en' ? 'Requests' : 'Solicitudes'}
                </div>
                <p className="font-heading text-lg font-bold">{stats.total_requests}</p>
                <p className="text-[10px] text-muted-foreground">{language === 'en' ? 'Tourist requests' : 'Pedidos de turistas'}</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-0.5">
                  <Users size={12} /> {language === 'en' ? 'Users' : 'Usuarios'}
                </div>
                <p className="font-heading text-lg font-bold">{stats.total_tourists + stats.total_providers}</p>
                <p className="text-[10px] text-muted-foreground">
                  {stats.total_tourists} {language === 'en' ? 'tourists' : 'turistas'} · {stats.total_providers} prov.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full inline-flex overflow-x-auto scrollbar-hide mb-4 h-10 justify-start">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="shrink-0">
              {language === 'en' ? 'Overview' : 'Resumen'}
            </TabsTrigger>
            <TabsTrigger value="tourists" data-testid="admin-tab-tourists" className="shrink-0">
              {language === 'en' ? 'Tourists' : 'Turistas'}
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests" className="shrink-0">
              {language === 'en' ? 'Requests' : 'Solicitudes'}
            </TabsTrigger>
            <TabsTrigger value="providers" data-testid="admin-tab-providers" className="shrink-0">
              {language === 'en' ? 'Providers' : 'Proveedores'}
            </TabsTrigger>
            <TabsTrigger value="partners" data-testid="admin-tab-partners" className="shrink-0">
              Partners
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="shrink-0">
              {language === 'en' ? 'Bookings' : 'Reservas'}
            </TabsTrigger>
            <TabsTrigger value="cities" data-testid="admin-tab-cities" className="shrink-0">
              {language === 'en' ? 'Cities' : 'Ciudades'}
            </TabsTrigger>
            <TabsTrigger value="promos" data-testid="admin-tab-promos" className="shrink-0">
              {language === 'en' ? 'Promos' : 'Promos'}
            </TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts" className="shrink-0">
              {language === 'en' ? 'Payouts' : 'Pagos a Partners'}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Requests */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText size={20} className="text-gold" />
                    {language === 'en' ? 'Recent Requests' : 'Solicitudes Recientes'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {requests.slice(0, 5).map((req) => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">
                            {CATEGORY_LABELS[req.category]?.[language] || req.category}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin size={12} />
                            {LOCATION_LABELS[req.location] || req.location}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant={req.status === 'active' ? 'default' : req.status === 'booked' ? 'secondary' : 'outline'}>
                            {req.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {req.offer_count} {language === 'en' ? 'offers' : 'ofertas'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Tourists */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus size={20} className="text-blue-600" />
                    {language === 'en' ? 'Recent Tourists' : 'Turistas Recientes'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tourists.slice(0, 5).map((tourist) => (
                      <div key={tourist.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">{tourist.name}</p>
                          <p className="text-sm text-muted-foreground">{tourist.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">
                            {formatDate(tourist.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tourists Tab */}
          <TabsContent value="tourists">
            <Card className="shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users size={20} className="text-blue-600" />
                  {language === 'en' ? 'Registered Tourists' : 'Turistas Registrados'}
                  <Badge variant="secondary" className="ml-2">{tourists.length}</Badge>
                </CardTitle>
                <Select value={touristFilter} onValueChange={setTouristFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'en' ? 'All Time' : 'Todos'}</SelectItem>
                    <SelectItem value="today">{language === 'en' ? 'Today' : 'Hoy'}</SelectItem>
                    <SelectItem value="week">{language === 'en' ? 'This Week' : 'Esta Semana'}</SelectItem>
                    <SelectItem value="month">{language === 'en' ? 'This Month' : 'Este Mes'}</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'Name' : 'Nombre'}</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>{language === 'en' ? 'Phone' : 'Teléfono'}</TableHead>
                        <TableHead>{language === 'en' ? 'Registered' : 'Registrado'}</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tourists.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            {language === 'en' ? 'No tourists found' : 'No se encontraron turistas'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        tourists.map((tourist) => (
                          <TableRow key={tourist.id}>
                            <TableCell className="font-medium">{tourist.name}</TableCell>
                            <TableCell>{tourist.email}</TableCell>
                            <TableCell>{tourist.phone || '-'}</TableCell>
                            <TableCell>{formatDateTime(tourist.created_at)}</TableCell>
                            <TableCell>
                              {tourist.is_active ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle size={12} className="mr-1" />
                                  {language === 'en' ? 'Active' : 'Activo'}
                                </Badge>
                              ) : (
                                <Badge variant="destructive">
                                  <XCircle size={12} className="mr-1" />
                                  {language === 'en' ? 'Inactive' : 'Inactivo'}
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            <Card className="shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText size={20} className="text-gold" />
                  {language === 'en' ? 'All Requests' : 'Todas las Solicitudes'}
                  <Badge variant="secondary" className="ml-2">{requestsDetailed.length}</Badge>
                </CardTitle>
                <Select value={requestFilter} onValueChange={setRequestFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'en' ? 'All Time' : 'Todos'}</SelectItem>
                    <SelectItem value="today">{language === 'en' ? 'Today' : 'Hoy'}</SelectItem>
                    <SelectItem value="week">{language === 'en' ? 'This Week' : 'Esta Semana'}</SelectItem>
                    <SelectItem value="month">{language === 'en' ? 'This Month' : 'Este Mes'}</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'Tourist' : 'Turista'}</TableHead>
                        <TableHead>{language === 'en' ? 'Category' : 'Categoría'}</TableHead>
                        <TableHead>{language === 'en' ? 'Location' : 'Ubicación'}</TableHead>
                        <TableHead>{language === 'en' ? 'Date' : 'Fecha'}</TableHead>
                        <TableHead>{language === 'en' ? 'People' : 'Personas'}</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>{language === 'en' ? 'Offers' : 'Ofertas'}</TableHead>
                        <TableHead>{language === 'en' ? 'Assigned Provider' : 'Proveedor Asignado'}</TableHead>
                        <TableHead>{language === 'en' ? 'Created' : 'Creado'}</TableHead>
                        <TableHead className="text-right">{language === 'en' ? 'Actions' : 'Acciones'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requestsDetailed.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                            {language === 'en' ? 'No requests found' : 'No se encontraron solicitudes'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        requestsDetailed.map((req) => (
                          <TableRow key={req.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{req.tourist_name}</p>
                                <p className="text-xs text-muted-foreground">{req.tourist_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {CATEGORY_LABELS[req.category]?.[language] || req.category}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1">
                                <MapPin size={12} className="text-muted-foreground" />
                                {LOCATION_LABELS[req.location] || req.location}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1">
                                <Calendar size={12} className="text-muted-foreground" />
                                {req.date}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1">
                                <Users size={12} className="text-muted-foreground" />
                                {req.people_count}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={req.status === 'active' ? 'default' : req.status === 'booked' ? 'secondary' : 'outline'}
                                className={req.status === 'booked' ? 'bg-green-100 text-green-800' : ''}
                              >
                                {req.status === 'active' && (language === 'en' ? 'Active' : 'Activa')}
                                {req.status === 'booked' && (language === 'en' ? 'Booked' : 'Reservada')}
                                {req.status === 'expired' && (language === 'en' ? 'Expired' : 'Expirada')}
                                {!['active', 'booked', 'expired'].includes(req.status) && req.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center font-semibold">
                              {req.offer_count}
                            </TableCell>
                            <TableCell>
                              {req.assigned_provider ? (
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-caribbean text-white">
                                    <CheckCircle size={12} className="mr-1" />
                                    {req.assigned_provider}
                                  </Badge>
                                  {req.accepted_price && (
                                    <span className="text-sm font-semibold text-green-600">
                                      ${req.accepted_price}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">
                                  {language === 'en' ? 'Not assigned' : 'Sin asignar'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(req.created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteRequest(req.id)}
                                data-testid={`delete-request-${req.id}`}
                                className="text-destructive hover:bg-destructive/10"
                                title={language === 'en' ? 'Delete request' : 'Borrar solicitud'}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield size={20} className="text-caribbean" />
                  {language === 'en' ? 'All Providers' : 'Todos los Proveedores'}
                  <Badge variant="secondary" className="ml-2">{providers.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'Name' : 'Nombre'}</TableHead>
                        <TableHead>{language === 'en' ? 'Company' : 'Empresa'}</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>{language === 'en' ? 'Reviews' : 'Reseñas'}</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>{language === 'en' ? 'Docs' : 'Docs'}</TableHead>
                        <TableHead>{language === 'en' ? 'Actions' : 'Acciones'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {providers.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell className="font-medium">{provider.name}</TableCell>
                          <TableCell>{provider.company_name || '-'}</TableCell>
                          <TableCell>{provider.email}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              <span className="text-yellow-500">★</span>
                              {provider.rating?.toFixed(1) || '0.0'}
                            </span>
                          </TableCell>
                          <TableCell>{provider.review_count || 0}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {provider.is_verified && (
                                <Badge className="bg-caribbean">
                                  <Shield size={12} className="mr-1" />
                                  {language === 'en' ? 'Verified' : 'Verificado'}
                                </Badge>
                              )}
                              {!provider.is_active && (
                                <Badge variant="destructive">
                                  {language === 'en' ? 'Banned' : 'Baneado'}
                                </Badge>
                              )}
                              {!provider.is_verified && provider.is_active && (
                                <Badge variant="outline">
                                  {language === 'en' ? 'Pending' : 'Pendiente'}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 text-xs">
                              {provider.id_document ? (
                                <a
                                  href={photoSrc(provider.id_document)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`provider-id-${provider.id}`}
                                  className="inline-flex items-center gap-1 text-caribbean hover:underline"
                                >
                                  <FileText size={12} /> INE
                                </a>
                              ) : (
                                <span className="text-muted-foreground">— INE</span>
                              )}
                              {provider.tax_document ? (
                                <a
                                  href={photoSrc(provider.tax_document)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`provider-rfc-${provider.id}`}
                                  className="inline-flex items-center gap-1 text-caribbean hover:underline"
                                >
                                  <FileText size={12} /> RFC
                                </a>
                              ) : (
                                <span className="text-muted-foreground">— RFC</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {!provider.is_verified && provider.is_active && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleVerifyProvider(provider.id)}
                                  className="text-caribbean"
                                >
                                  <Shield size={14} className="mr-1" />
                                  {language === 'en' ? 'Verify' : 'Verificar'}
                                </Button>
                              )}
                              {provider.is_active && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleBanProvider(provider.id)}
                                  className="text-destructive"
                                >
                                  <Ban size={14} className="mr-1" />
                                  Ban
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteProvider(provider.id, provider.name)}
                                data-testid={`delete-provider-${provider.id}`}
                                className="text-destructive hover:bg-destructive/10"
                                title={language === 'en' ? 'Delete provider' : 'Borrar proveedor'}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HandCoins size={20} className="text-rosa" />
                  {language === 'en' ? 'All Partners' : 'Todos los Partners'}
                  <Badge variant="secondary" className="ml-2">{partners.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'Name' : 'Nombre'}</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>{language === 'en' ? 'Compa code' : 'Código compa'}</TableHead>
                        <TableHead>{language === 'en' ? 'Referrals' : 'Referidos'}</TableHead>
                        <TableHead>{language === 'en' ? 'Total commission' : 'Comisión total'}</TableHead>
                        <TableHead>{language === 'en' ? 'Stripe' : 'Stripe'}</TableHead>
                        <TableHead>{language === 'en' ? 'Joined' : 'Se unió'}</TableHead>
                        <TableHead className="text-right">{language === 'en' ? 'Actions' : 'Acciones'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partners.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            {language === 'en' ? 'No partners yet' : 'Aún no hay partners'}
                          </TableCell>
                        </TableRow>
                      ) : partners.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="text-sm">{p.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">
                              {p.compa_code || p.referral_code || '—'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center font-semibold">{p.referrals || 0}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            ${(p.total_commission || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {p.stripe_account_id ? (
                              <Badge className="bg-caribbean text-white">
                                <CheckCircle size={12} className="mr-1" />
                                {language === 'en' ? 'Connected' : 'Conectado'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground">
                                {language === 'en' ? 'Not linked' : 'Sin vincular'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(p.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeletePartner(p.id, p.name)}
                              data-testid={`delete-partner-${p.id}`}
                              className="text-destructive hover:bg-destructive/10"
                              title={language === 'en' ? 'Delete partner' : 'Borrar partner'}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBag size={20} className="text-rosa" />
                  {language === 'en' ? 'All Bookings' : 'Todas las Reservas'}
                  <Badge variant="secondary" className="ml-2">{bookings.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-3 rounded-xl bg-caribbean/5 border border-caribbean/20 p-3 text-xs">
                  <p className="font-semibold text-caribbean mb-1 flex items-center gap-1">
                    <Receipt size={12} /> {language === 'en' ? 'How each booking splits' : 'Cómo se divide cada reserva'}
                  </p>
                  <p className="text-muted-foreground leading-snug">
                    {language === 'en'
                      ? 'Tourist pays only 17% + 16% IVA upfront (Stripe). The remaining 80.28% is paid in cash directly to the provider on arrival.'
                      : 'El turista paga sólo 17% + 16% IVA por adelantado (Stripe). El 80.28% restante se paga en efectivo al proveedor al llegar.'}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>{language === 'en' ? 'Provider' : 'Proveedor'}</TableHead>
                        <TableHead>{language === 'en' ? 'Total' : 'Total'}</TableHead>
                        <TableHead className="text-caribbean">{language === 'en' ? 'Reserved (17%+IVA)' : 'Reservación (17%+IVA)'}</TableHead>
                        <TableHead>{language === 'en' ? 'Cash to provider' : 'En efectivo'}</TableHead>
                        <TableHead>{language === 'en' ? 'Payment' : 'Pago'}</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>{language === 'en' ? 'Date' : 'Fecha'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((booking) => (
                        <TableRow key={booking.id}>
                          <TableCell className="font-mono text-xs">{booking.id.slice(0, 8)}</TableCell>
                          <TableCell className="font-medium">{booking.provider_name || '-'}</TableCell>
                          <TableCell className="font-semibold">${booking.total_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-caribbean font-semibold">
                            ${(booking.reservation_fee ?? booking.platform_fee ?? 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            ${(booking.provider_balance_due ?? 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={booking.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {booking.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={booking.status === 'completed' ? 'default' : 'secondary'}
                              className={booking.status === 'completed' ? 'bg-green-100 text-green-800' : ''}
                            >
                              {booking.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(booking.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cities Tab */}
          <TabsContent value="cities">
            <AdminCityContent />
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promos">
            <AdminPromoBroadcast api={api} language={language} />
          </TabsContent>

          {/* Partner Payouts Tab */}
          <TabsContent value="payouts">
            <AdminPartnerPayouts api={api} language={language} />
          </TabsContent>
        </Tabs>
      </main>
    </MobileShell>
  );
}

function AdminPromoBroadcast({ api, language }) {
  const [form, setForm] = useState({
    title_es: '', title_en: '', body_es: '', body_en: '',
    target_role: 'tourist', link: '',
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const send = async () => {
    if (!form.title_es || !form.title_en || !form.body_es || !form.body_en) {
      toast.error(language === 'en' ? 'Fill all fields in both languages' : 'Completa todos los campos en ambos idiomas');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const { data } = await api().post('/admin/notifications/broadcast', {
        ...form,
        link: form.link || null,
      });
      setResult(data);
      toast.success(language === 'en'
        ? `Sent to ${data.sent} users`
        : `Enviado a ${data.sent} usuarios`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  const inputClass = "w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-caribbean";

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign size={18} className="text-rosa" />
          {language === 'en' ? 'Send a Chuy notification' : 'Mandar notificación de Chuy'}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {language === 'en'
            ? 'Push a promo or announcement to every user of a given role. Each receives it from Chuy in their language.'
            : 'Manda una promo o anuncio a todos los usuarios de un rol. Cada quien la recibe de Chuy en su idioma.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase text-muted-foreground">{language === 'en' ? 'Recipients' : 'Destinatarios'}</label>
          <select
            data-testid="promo-target"
            className={inputClass + ' mt-1'}
            value={form.target_role}
            onChange={(e) => update('target_role', e.target.value)}
          >
            <option value="tourist">{language === 'en' ? 'Tourists' : 'Turistas'}</option>
            <option value="provider">{language === 'en' ? 'Providers' : 'Proveedores'}</option>
            <option value="partner">{language === 'en' ? 'Partners' : 'Partners'}</option>
            <option value="all">{language === 'en' ? 'Everyone' : 'Todos'}</option>
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground">Título (ES) 🇲🇽</label>
            <input data-testid="promo-title-es" className={inputClass + ' mt-1'} placeholder="🎉 15% en yates hoy" value={form.title_es} onChange={(e) => update('title_es', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground">Title (EN) 🇺🇸</label>
            <input data-testid="promo-title-en" className={inputClass + ' mt-1'} placeholder="🎉 15% off yachts today" value={form.title_en} onChange={(e) => update('title_en', e.target.value)} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground">Mensaje (ES)</label>
            <textarea data-testid="promo-body-es" rows={3} className={inputClass + ' mt-1'} placeholder="¡Échale ojo compa! Sólo hoy…" value={form.body_es} onChange={(e) => update('body_es', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-muted-foreground">Message (EN)</label>
            <textarea data-testid="promo-body-en" rows={3} className={inputClass + ' mt-1'} placeholder="Heads up partner! Today only…" value={form.body_en} onChange={(e) => update('body_en', e.target.value)} />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase text-muted-foreground">{language === 'en' ? 'Optional link' : 'Enlace opcional'}</label>
          <input data-testid="promo-link" className={inputClass + ' mt-1'} placeholder="/catalog?category=yachts" value={form.link} onChange={(e) => update('link', e.target.value)} />
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {language === 'en' ? 'When users tap the notification, they\'ll land here.' : 'Cuando toquen la notificación, llegarán aquí.'}
          </p>
        </div>

        <Button
          data-testid="promo-send"
          onClick={send}
          disabled={sending}
          className="w-full h-11 rounded-full bg-rosa hover:bg-rosa/90 text-white font-semibold"
        >
          {sending
            ? (language === 'en' ? 'Sending…' : 'Enviando…')
            : (language === 'en' ? 'Send notification' : 'Enviar notificación')}
        </Button>

        {result && (
          <div className="rounded-xl bg-caribbean/5 border border-caribbean/30 p-3 text-xs">
            <p className="font-semibold text-caribbean">
              ✅ {language === 'en' ? `Sent to ${result.sent} users` : `Enviado a ${result.sent} usuarios`}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminPartnerPayouts({ api, language }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api().get('/admin/partner-payouts');
        setData(data);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  if (loading) return <p className="text-sm text-muted-foreground">{language === 'en' ? 'Loading…' : 'Cargando…'}</p>;
  if (!data) return null;

  const fmt = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="space-y-5">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-[#635BFF] to-[#3F32D6] p-5 text-white shadow-xl">
        <p className="text-xs opacity-80 uppercase tracking-wider font-semibold flex items-center gap-1.5">
          <CreditCard size={12} /> {language === 'en' ? 'Total paid to partners' : 'Total pagado a partners'}
        </p>
        <p className="font-heading text-4xl font-bold mt-1">
          ${data.total_transferred?.toFixed(2) ?? '0.00'} <span className="text-sm font-normal opacity-80">USD</span>
        </p>
        <p className="text-[11px] opacity-80 mt-1">
          {data.transfers_count} {language === 'en' ? 'transfers via Stripe Connect' : 'transferencias vía Stripe Connect'}
        </p>
      </div>

      {/* Top 10 */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp size={18} className="text-rosa" />
            {language === 'en' ? 'Top 10 partners' : 'Top 10 partners'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.top_partners?.length === 0 && (
            <p className="text-sm text-muted-foreground">{language === 'en' ? 'No partner commissions yet.' : 'Aún sin comisiones de partner.'}</p>
          )}
          <div className="space-y-2">
            {data.top_partners?.map((p, i) => (
              <div key={p.partner_id} data-testid={`top-partner-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-muted/40">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-gold text-white' : i < 3 ? 'bg-rosa text-white' : 'bg-foreground/10 text-foreground'}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm truncate">{p.name || p.email}</p>
                    {p.stripe_ready ? (
                      <Badge className="bg-caribbean text-white text-[9px] px-1.5 py-0">Stripe ✅</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0">Sin Stripe</Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {p.email} · {p.codigo_compa} · {p.bookings_count} {language === 'en' ? 'bookings' : 'reservas'}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-heading text-sm font-bold text-caribbean">${p.total_earned?.toFixed(2)}</p>
                  {p.pending > 0 && (
                    <p className="text-[10px] text-amber-600">⏳ ${p.pending?.toFixed(2)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transfers history */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt size={18} className="text-caribbean" />
            {language === 'en' ? 'Transfers history' : 'Historial de transferencias'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.transfers?.length === 0 && (
            <p className="text-sm text-muted-foreground">{language === 'en' ? 'No transfers yet. Partners need to connect Stripe first.' : 'Aún sin transferencias. Los partners necesitan conectar su Stripe primero.'}</p>
          )}
          <div className="overflow-x-auto">
            {data.transfers?.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{language === 'en' ? 'When' : 'Cuándo'}</TableHead>
                    <TableHead>{language === 'en' ? 'Partner' : 'Partner'}</TableHead>
                    <TableHead className="text-right">{language === 'en' ? 'Amount' : 'Monto'}</TableHead>
                    <TableHead>Stripe ID</TableHead>
                    <TableHead>Booking</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.transfers.map((t, i) => (
                    <TableRow key={i} data-testid={`payout-row-${i}`}>
                      <TableCell className="text-xs text-muted-foreground">{fmt(t.transferred_at)}</TableCell>
                      <TableCell>
                        <div className="leading-tight">
                          <p className="font-semibold text-sm">{t.partner_name || '—'}</p>
                          <p className="text-[10px] text-muted-foreground">{t.partner_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold text-caribbean">${(t.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground">{t.stripe_transfer_id?.slice(0, 14)}…</TableCell>
                      <TableCell className="font-mono text-[10px] text-muted-foreground">{t.booking_id?.slice(0, 8)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
